import type { SubmitEventHandler } from 'react';
import type { SupportedLanguage } from '../../../lib/language';

import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import z from 'zod';

import { setFormSubmitErrors } from '../../../form-hooks/lib/helpers';
import { getTestProps } from '../../../lib/helpers';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
} from '../../../lib/language';
import { emailSchema, passwordSchema } from '../../../lib/zodHelpers';

import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';

import {
  PolandFlagIcon,
  UnitedKingdomFlagIcon,
  WarningIcon,
} from '../../../images/shapes';

import { useAppForm } from '../../../form-hooks/form';

export type RegisterFormSubmitFn = (data: {
  firstName: string;
  lastName: string;
  email: string;
  plainPassword: string;
  language: SupportedLanguage;
  privacyPolicy: boolean;
  termsOfService: boolean;
  recaptchaToken?: string;
}) => Promise<Record<string, string | string[]> | undefined>;

export type RegisterFormBaseProps = {
  /**
   * ReCaptcha execute function
   */
  executeRecaptcha?: (action?: string) => Promise<string>;
  /**
   * On submit handler
   */
  onSubmit: RegisterFormSubmitFn;
  /**
   * Default email form value
   */
  prefilledEmail?: string;
  /**
   * Form test id
   */
  testId?: string;
};

const RegisterFormBase = ({
  onSubmit,
  prefilledEmail = '',
  testId,
  executeRecaptcha,
}: RegisterFormBaseProps) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | string[]>('');

  const lngOptions = useMemo(
    () => [
      {
        value: 'en',
        searchString: t('UserForm.English'),
        label: (
          <div className="flex flex-row items-center">
            <UnitedKingdomFlagIcon className="w-5 h-5 mr-2" />
            {t('UserForm.English')}
          </div>
        ),
      },
      {
        value: 'pl',
        searchString: t('UserForm.Polish'),
        label: (
          <div className="flex flex-row items-center">
            <PolandFlagIcon className="w-5 h-5 mr-2" />
            {t('UserForm.Polish')}
          </div>
        ),
      },
    ],
    [t],
  );

  const defaultLanguage = useMemo<SupportedLanguage>(() => {
    const current = i18n.resolvedLanguage ?? i18n.language ?? '';
    return isSupportedLanguage(current) ? current : DEFAULT_LANGUAGE;
  }, [i18n.resolvedLanguage, i18n.language]);

  const validationSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t('Form.FormErrorNotBlank')),
        lastName: z.string().min(1, t('Form.FormErrorNotBlank')),
        email: emailSchema(t),
        plainPassword: passwordSchema(t),
        language: z.enum(SUPPORTED_LANGUAGES, {
          message: t('Form.FormErrorNotBlank'),
        }),
      }),
    [t],
  );

  const initialValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: prefilledEmail ?? '',
      plainPassword: '',
      language: defaultLanguage,
    }),
    [prefilledEmail, defaultLanguage],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      if (!onSubmit) return;

      setGeneralError('');
      setIsLoading(true);

      try {
        let recaptchaToken: string | undefined;
        if (executeRecaptcha) {
          try {
            recaptchaToken = await executeRecaptcha('submit');
          } catch (error) {
            console.error('ReCaptcha execution error:', error);
            recaptchaToken = undefined;
          }
          if (!recaptchaToken) {
            setGeneralError(t('Form.RecaptchaError'));
            return;
          }
        }

        const errors = await onSubmit({
          ...value,
          privacyPolicy: true,
          termsOfService: true,
          recaptchaToken,
        });

        if (errors && Object.keys(errors).length > 0) {
          if (errors.general) {
            setGeneralError(errors.general);
          }
          setFormSubmitErrors(formApi, errors);
        }
      } finally {
        setIsLoading(false);
      }
    },
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="register-form"
        className="w-full"
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId)}
      >
        <div className="grid grid-cols-2 gap-4 2xl:gap-6 pb-6">
          <form.AppField name="firstName">
            {(field) => (
              <field.Input
                placeholder={t('Global.FirstName')}
                {...getTestProps(testId, 'firstName', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="lastName">
            {(field) => (
              <field.Input
                placeholder={t('Global.LastName')}
                {...getTestProps(testId, 'lastName', 'testId')}
              />
            )}
          </form.AppField>
        </div>
        <div className="flex flex-wrap gap-4 2xl:gap-6 ">
          <form.AppField name="email">
            {(field) => (
              <field.Input
                placeholder={t('Global.Email')}
                additionalInputClasses={
                  prefilledEmail ? 'cursor-not-allowed' : ''
                }
                disabled={!!prefilledEmail}
                {...getTestProps(testId, 'email', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="plainPassword">
            {(field) => (
              <field.Input
                type="password"
                placeholder={t('Global.Password')}
                showPasswordInfo
                {...getTestProps(testId, 'password', 'testId')}
              />
            )}
          </form.AppField>
          <form.AppField name="language">
            {(field) => (
              <field.Dropdown
                placeholder={t('UserForm.Language')}
                options={lngOptions}
                additionalClasses="w-full"
                hideSearch
                {...getTestProps(testId, 'language', 'testId')}
              />
            )}
          </form.AppField>
        </div>
        <div className="dark:text-white my-8 lg:my-5 2xl:my-8 text-sm md:text-base opacity-70 text-center">
          <Trans
            i18nKey="Global.DataProcessing"
            components={{
              1: (
                <Link
                  className="text-blue underline"
                  to="https://flotiq.com/terms-of-service/"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
              2: (
                <Link
                  className="text-blue underline"
                  to="https://flotiq.com/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
        <div className="w-full flex flex-col justify-center text-center">
          <Button
            id="button_register_with_email"
            buttonSize="lg"
            type="submit"
            additionalClasses="w-full md:min-w-[300px] justify-center"
            disabled={isLoading}
            iconImage={
              isLoading ? <Loader size="small" type="spinner-grid" /> : null
            }
            {...getTestProps(testId, 'submit', 'testId')}
          >
            {t('Global.CreateAnAccount')}
          </Button>
          {generalError && (
            <div
              className="text-red py-2 inline-flex items-center"
              {...getTestProps(testId, 'general-error')}
            >
              <WarningIcon className="h-5 w-5 mr-2" />
              <div className="block text-left">{generalError}</div>
            </div>
          )}
        </div>
      </form>
    </form.AppForm>
  );
};

export default RegisterFormBase;
